import { Box, styled } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { gridClasses } from '@mui/x-data-grid';
import {
  ClientDTO,
  ClientRequestDTO,
  ClientStatusDTO,
  ColumnSettings,
  getColumnSettings,
  ILocation,
  isoCountries,
  saveColumnSettings,
} from 'wisestep-api';
import { FieldOption, WsColors } from 'wisestep-base';

import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { success, failed } from '../../../utils/NotificationMessages';
import { INotification } from '../../../utils/NotificationStore';
import { legalIdentifierDefaultPayload } from '../../clientsSettings/helper/ClientSettingsHelper';

export const defaultClientStatusDTO: ClientStatusDTO = {
  id: '',
  clientStatusName: '',
};

export const defaultClientDTO: ClientDTO = {
  name: '',
  legalName: '',
  industry: '',
  website: '',
  timezone: '',
  invoiceCode: '',
  paymentTerms: '',
  address: '',
  description: '',
  createdByName: '',
  country: '',
  branch: '',
  source: '',
  isActive: true,
  id: '',
  parentId: '',
  createdBy: '',
  createdOn: null,
  businessUnit: null,
  childCount: 0,
  location: null,
  clientStatusId: '',
  clientStatus: defaultClientStatusDTO,
  clientStatusReason: '',
  clientContactCount: 0,
  tags: [],
};

export const getDefaultClientBank = (clientId: string) => ({
  data: {
    id: '',
    bankName: '',
    branchName: '',
    bankCode: '',
    bankAccountNumber: '',
    currencyId: '',
    iban: '',
    swiftCode: '',
    clientId: clientId ?? '',
    isActive: true,
  },
  isEditable: true,
});

export const defaultLegalData = (clientId: string) => ({
  data: {
    id: null,
    legalIdentifierDTO: { ...legalIdentifierDefaultPayload, name: '', id: '' },
    clientId: clientId ?? '',
    value: '',
    createdBy: '',
    modifiedBy: '',
    modifiedOn: null,
    createdOn: null,
  },
  isEditable: true,
});

export const defaultClientContactDTO = (clientId, input?: string) => ({
  id: null,
  firstName: input?.split(/[ @]/)[0] ?? '',
  lastName: input?.split(' ')[1] ?? '',
  emailAddress: input?.includes('@') ? input : '',
  phoneNumber: '',
  physicalAddress: '',
  position: '',
  linkedinProfile: '',
  supervisorPosition: '',
  supervisorName: '',
  createdByName: '',
  markStatus: true,
  workspaceId: '',
  clientId: clientId ?? '',
  createdBy: '',
  createdOn: null,
  tags: [],
  location: null,
});

export const getClientLocation = (clientDTO: ClientDTO) => {
  let location: string | null = null;
  if (clientDTO.branch) {
    location = clientDTO.branch;
  }
  if (isoCountries[clientDTO.country]) {
    location = (location ? location + ', ' : '') + isoCountries[clientDTO.country];
  }
  return location;
};

export const initialLocation: ILocation = {
  continent: '',
  continentCode: '',
  city: '',
  state: '',
  country: '',
  countryCode: '',
  stateCode: '',
};

export interface IFilters {
  search: string;
  status: string[] | null;
  location?: ILocation[];
  tags: string[];
}

export const defaultFilters: ClientRequestDTO = {
  query: '',
  status: null,
  location: initialLocation[0],
  tags: [],
  pageNo: 0,
  pageSize: 50,
  sortBy: 'createdOn',
  sortOrder: 'asc',
  source: [],
  clientOwners: [],
  createdByIds: [],
  fromDate: '',
  toDate: '',
  industryType: [],
};

export const mapToClientAdvanceFilter = (data): ClientRequestDTO => {
  return {
    pageNo: 0,
    pageSize: 25,
    sortOrder: 'asc',
    sortBy: 'createdOn',
    status: data.status ?? [],
    query: data.query ?? '',
    tags: data.tags ?? [],
    filterByDateType: data.filterByDateType === 'UPDATED_BETWEEN' ? 'modified_on' : 'created_on',
    fromDate: data.fromDate,
    toDate: data.toDate,
    createdByIds: data.createdByIds,
    industryType: data.industryType ?? [],
    clientOwners: data.clientOwners ?? [],
    location: data.location,
    source: data.source,
  };
};

export const getCommonGridStyles = (): SxProps<Theme> => ({
  [`& .${gridClasses.cell}`]: {
    py: 1,
  },
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.row}:hover`]: {
    backgroundColor: 'inherit',
  },
});

export const linkedInRegex =
  /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/(in|company)\/(?:[a-zA-Z0-9&-.]+|%[0-8][0-9A-Fa-f])+(\/(?:[a-zA-Z0-9&-.]+|%[0-8][0-9A-Fa-f])*)?\/?(\?.*)?$/;

export const phoneNumberRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

export const websiteRegex = /^(?:https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/\S*)?$/;

export const getEncodedBreadCrumbs = (
  prevBreadCrumbs: IBreadCrumbsList[],
  clientName: string
): string =>
  getEncodedQueryParams([
    ...prevBreadCrumbs,
    { label: clientName, url: `${window.location.pathname}${window.location.search}` },
  ]);

export const StyledBox = styled(Box)(() => ({
  backgroundColor: WsColors.White,
  border: `1px solid ${WsColors.Gray88}`,
  padding: 16,
  borderTopLeftRadius: 2,
  borderTopRightRadius: 2,
  borderBottom: `1px solid ${WsColors.Gray88}`,
}));

export const clientTypes = ['All Clients', 'My Clients'];

export const defaultColumnSettings = (): FieldOption[] => [
  { id: 'name', checked: true, required: true },
  { id: 'clientStatusName', checked: true },
  { id: 'childCount', checked: true },
  { id: 'owner', checked: true },
  { id: 'parentName', checked: false },
  { id: 'clientContactCount', checked: true },
  { id: 'country', checked: true },
  { id: 'industry', checked: false },
  { id: 'source', checked: false },
  { id: 'paymentTerms', checked: false },
  { id: 'website', checked: false },
  { id: 'tags', checked: false },
  { id: 'created_by_name', checked: true },
  { id: 'created_on', checked: true },
  { id: 'action', checked: true, required: true },
];

export const fetchColumnSettings = async (tableKey: ColumnSettings, reqFields: string[]) => {
  try {
    const res = await getColumnSettings(tableKey);
    if (Array.isArray(res.data?.settings.column)) {
      const settingsWithReqFields: FieldOption[] = (res.data.settings.column as FieldOption[]).map(
        (setting) => {
          const isRequired = reqFields.includes(setting.id);
          return {
            ...setting,
            checked: setting.checked || isRequired,
            required: isRequired,
          };
        }
      );
      const remainingReqFields = reqFields
        .filter((field) => !settingsWithReqFields.some((setting) => setting.id == field))
        .map((field) => ({
          id: field,
          checked: true,
          required: true,
        }));
      return [...settingsWithReqFields, ...remainingReqFields];
    }
  } catch (error) {
    console.error('Error fetching column settings data', error);
  }
};

export const saveColumnSettingsToDB = async (
  columnData: FieldOption[],
  tableKey: ColumnSettings,
  setNotification: (notification: INotification) => void
) => {
  const columnSettings = columnData.map(({ id, checked }) => ({ id, checked }));
  const settingsPayload = {
    settings: { column: columnSettings },
    tableKey,
  };
  try {
    await saveColumnSettings(settingsPayload as any);
    setNotification(success('Column Settings Saved Successfully'));
  } catch (err) {
    setNotification(failed('Error Saving Column Settings !'));
    console.error('error saving column settings ', err);
  }
};
